import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import { Empenho, EmpenhoFormatado } from "../../../types/empenhos";
import { apiService } from "../../../services/api";
import ModalEmpenho from "./ModalEmpenho";
import ModalGerarEmpenho from "./ModalGerarEmpenho";
import {
    getEtapaLabel,
    getEtapaBadge
} from "../../crm/empenhos/utils/etapaUtils";
import { usePermissions } from "../../../hooks/usePermissions";
import { ProtectedValue } from "../../../components/ProtectedValue";

interface EmpenhosListProps {
    contratoId: number;
    formatCurrency: (value: number) => string;
    formatDate: (dateString: string) => string;
    onUpdateComplete: () => void;
}

const EmpenhosList: React.FC<EmpenhosListProps> = ({
    contratoId,
    formatCurrency,
    formatDate,
    onUpdateComplete
}) => {
    const { hasPermission } = usePermissions();
    const podeVerValores = hasPermission('AUTORIZACOES', 'VER_VALORES');

    const [empenhos, setEmpenhos] = useState<EmpenhoFormatado[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedEmpenho, setSelectedEmpenho] = useState<EmpenhoFormatado | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showGerarModal, setShowGerarModal] = useState(false);
    const [masterParaGerar, setMasterParaGerar] = useState<EmpenhoFormatado | null>(null);
    const [expandido, setExpandido] = useState<{ [key: number]: boolean }>({});
    const [produtosContrato, setProdutosContrato] = useState<any[]>([]);
    const [empenhosDetalhados, setEmpenhosDetalhados] = useState<{ [key: number]: any[] }>({});

    // Carregar produtos do contrato
    const loadProdutosContrato = async () => {
        try {
            const contratoRes = await apiService.getContratoById(contratoId);
            if (contratoRes.success && contratoRes.data && contratoRes.data.id_oportunidade) {
                const produtosRes = await apiService.getProdutosOportunidade(contratoRes.data.id_oportunidade);
                if (produtosRes.success && produtosRes.data) {
                    setProdutosContrato(produtosRes.data);
                }
            }
        } catch (error) {
            console.error("Erro ao carregar produtos do contrato:", error);
        }
    };

    // Carregar empenhos do contrato
    const loadEmpenhos = async () => {
        try {
            setLoading(true);
            const empenhosRes = await apiService.getEmpenhos({ id_contrato: contratoId });

            if (empenhosRes.success && empenhosRes.data) {
                const empenhosFormatados = empenhosRes.data.map((empenho: Empenho) => ({
                    ...empenho,
                    numero_empenho: empenho.codigo_empenho || `EMP-${empenho.id}`,
                    status: empenho.situacao
                }));
                setEmpenhos(empenhosFormatados);

                // Carregar detalhes de cada empenho (itens)
                const detalhesPromises = empenhosFormatados.map(async (empenho: EmpenhoFormatado) => {
                    try {
                        const detalhesRes = await apiService.getEmpenhoById(empenho.id);
                        if (detalhesRes.success && detalhesRes.data) {
                            return { id: empenho.id, itens: detalhesRes.data.itens || [] };
                        }
                    } catch (error) {
                        console.error(`Erro ao carregar detalhes do empenho ${empenho.id}:`, error);
                    }
                    return { id: empenho.id, itens: [] };
                });

                const detalhesResultados = await Promise.all(detalhesPromises);
                const empenhosDetalhesMap = detalhesResultados.reduce((acc: { [key: number]: any[] }, resultado) => {
                    acc[resultado.id] = resultado.itens;
                    return acc;
                }, {});

                setEmpenhosDetalhados(empenhosDetalhesMap);
            }

            await loadProdutosContrato();
        } catch (error) {
            console.error("Erro ao carregar empenhos:", error);
            toast.error("Erro ao carregar empenhos");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEmpenhos();
    }, [contratoId]);

    // Separar empenhos em categorias
    const masters = empenhos.filter(e => e.is_master);
    const legados = empenhos.filter(e => !e.is_master && !e.id_empenho_pai);
    const filhosPorMaster: { [masterId: number]: EmpenhoFormatado[] } = {};
    empenhos.filter(e => !e.is_master && e.id_empenho_pai).forEach(filho => {
        const masterId = filho.id_empenho_pai!;
        if (!filhosPorMaster[masterId]) {
            filhosPorMaster[masterId] = [];
        }
        filhosPorMaster[masterId].push(filho);
    });

    // Funcoes de manipulacao
    const handleEdit = (empenho: EmpenhoFormatado) => {
        setSelectedEmpenho(empenho);
        setShowEditModal(true);
    };

    const handleDelete = (empenho: EmpenhoFormatado) => {
        setSelectedEmpenho(empenho);
        setShowDeleteModal(true);
    };

    const handleAddEmpenho = () => {
        setSelectedEmpenho(null);
        setShowAddModal(true);
    };

    const handleGerarEmpenho = (master: EmpenhoFormatado) => {
        setMasterParaGerar(master);
        setShowGerarModal(true);
    };

    const confirmDelete = async () => {
        if (!selectedEmpenho) return;
        try {
            await apiService.deleteEmpenho(selectedEmpenho.id);
            toast.success("Empenho excluido com sucesso!");
            loadEmpenhos();
            onUpdateComplete();
            setShowDeleteModal(false);
        } catch (error) {
            console.error("Erro ao excluir empenho:", error);
            toast.error("Erro ao excluir empenho");
        }
    };

    const handleEmpenhoSaved = () => {
        loadEmpenhos();
        onUpdateComplete();
        setShowEditModal(false);
        setShowAddModal(false);
        setShowGerarModal(false);
    };

    const toggleExpandido = (id: number) => {
        setExpandido(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Calcular progresso de um master
    const calcularProgressoMaster = (master: EmpenhoFormatado) => {
        const filhos = filhosPorMaster[master.id] || [];
        const valorGerado = master.valor_gerado_total
            ? Number(master.valor_gerado_total)
            : filhos.reduce((sum, f) => sum + Number(f.valor_total || 0), 0);
        const valorTotal = Number(master.valor_total || 0);
        const percentual = valorTotal > 0 ? (valorGerado / valorTotal) * 100 : 0;
        const count = master.filhos_count ?? filhos.length;

        return { valorGerado, percentual: Math.min(percentual, 100), count };
    };

    const calcularQuantidadeMaster = (master: EmpenhoFormatado) => {
        const itensMaster = empenhosDetalhados[master.id] || [];
        const totalPlanejado = itensMaster.reduce((sum: number, item: any) => sum + Number(item.quantidade || 0), 0);

        const filhos = filhosPorMaster[master.id] || [];
        const totalGerado = filhos.reduce((sum: number, filho) => {
            const itensFilho = empenhosDetalhados[filho.id] || [];
            return sum + itensFilho.reduce((s: number, item: any) => s + Number(item.quantidade || 0), 0);
        }, 0);

        return { totalPlanejado, totalGerado };
    };

    // Obter nome do produto
    const getNomeProduto = (idProduto: number) => {
        const produtoContrato = produtosContrato.find(p => p.id_produto === idProduto);
        return produtoContrato?.nome_produto || 'Produto';
    };

    return (
        <>
            <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                    <h4 className="fw-semibold">Empenhos</h4>
                    <div>
                        <button
                            className="btn btn-primary"
                            onClick={handleAddEmpenho}
                        >
                            <i className="ti ti-plus me-1"></i>
                            Adicionar Empenho
                        </button>
                    </div>
                </div>
                <div className="card-body">
                    {loading ? (
                        <div className="text-center p-3">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : masters.length === 0 && legados.length === 0 ? (
                        <div className="alert alert-info">
                            Nenhum empenho registrado para este contrato.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped custom-table mb-0">
                                <thead>
                                    <tr>
                                        <th>Empenho</th>
                                        <th>Valor Total</th>
                                        <th>Progresso</th>
                                        <th>Empenhos Gerados</th>
                                        <th>Acoes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* EMPENHOS MESTRE */}
                                    {masters.map((master) => {
                                        const { valorGerado, percentual, count } = calcularProgressoMaster(master);
                                        const { totalPlanejado, totalGerado } = calcularQuantidadeMaster(master);
                                        const isAberto = expandido[master.id] || false;
                                        const filhos = filhosPorMaster[master.id] || [];

                                        return (
                                            <React.Fragment key={`master-${master.id}`}>
                                                {/* Linha do Mestre */}
                                                <tr className="bg-light">
                                                    <td>
                                                        <div
                                                            className="d-flex align-items-center"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => toggleExpandido(master.id)}
                                                        >
                                                            {isAberto ? (
                                                                <i className="ti ti-chevron-up me-2"></i>
                                                            ) : (
                                                                <i className="ti ti-chevron-down me-2"></i>
                                                            )}
                                                            <div>
                                                                <span className="fw-bold">
                                                                    {master.codigo_empenho || `EMP-${master.id}`}
                                                                </span>
                                                                <br />
                                                                <small className="text-muted">
                                                                    <span className={`badge ${getEtapaBadge('PLANEJAMENTO')}`}>
                                                                        Mestre
                                                                    </span>
                                                                    {' '}{formatDate(master.data_empenho)}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="align-middle fw-medium">
                                                        <ProtectedValue module="AUTORIZACOES" value={formatCurrency(Number(master.valor_total))} />
                                                    </td>
                                                    <td className="align-middle" style={{ minWidth: '180px' }}>
                                                        <div>
                                                            <div className="d-flex align-items-center">
                                                                <div className="progress flex-grow-1" style={{ height: '8px' }}>
                                                                    <div
                                                                        className={`progress-bar ${percentual >= 100 ? 'bg-success' : percentual > 0 ? 'bg-primary' : 'bg-secondary'}`}
                                                                        role="progressbar"
                                                                        style={{ width: `${percentual}%` }}
                                                                        aria-valuenow={percentual}
                                                                        aria-valuemin={0}
                                                                        aria-valuemax={100}
                                                                    ></div>
                                                                </div>
                                                                <span className="ms-2 fw-medium">{percentual.toFixed(0)}%</span>
                                                            </div>
                                                            <small className="text-muted d-block">
                                                                <ProtectedValue module="AUTORIZACOES" value={formatCurrency(valorGerado)} /> de <ProtectedValue module="AUTORIZACOES" value={formatCurrency(Number(master.valor_total))} />
                                                            </small>
                                                            {totalPlanejado > 0 && (
                                                                <small className="text-muted">
                                                                    {totalGerado} un geradas / {totalPlanejado} un planejadas
                                                                </small>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="align-middle">
                                                        <span className="badge bg-info">{count} empenho(s)</span>
                                                    </td>
                                                    <td className="align-middle">
                                                        <div className="d-flex gap-1">
                                                            <button
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => handleGerarEmpenho(master)}
                                                                title="Gerar AF"
                                                            >
                                                                <i className="ti ti-send me-1"></i>
                                                                Gerar AF
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => handleEdit(master)}
                                                                title="Editar"
                                                            >
                                                                <i className="ti ti-edit"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDelete(master)}
                                                                title="Excluir"
                                                            >
                                                                <i className="ti ti-trash"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>

                                                {/* Filhos do mestre */}
                                                {isAberto && filhos.length > 0 && filhos.map((filho) => (
                                                    <React.Fragment key={`filho-${filho.id}`}>
                                                        <tr>
                                                            <td className="ps-4">
                                                                <div className="d-flex align-items-center">
                                                                    <i className="ti ti-corner-down-right text-muted me-2"></i>
                                                                    <div>
                                                                        <span>{filho.codigo_empenho || `EMP-${filho.id}`}</span>
                                                                        <br />
                                                                        <small className="text-muted">{formatDate(filho.data_empenho)}</small>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="align-middle">
                                                                <ProtectedValue module="AUTORIZACOES" value={formatCurrency(Number(filho.valor_total))} />
                                                            </td>
                                                            <td className="align-middle">
                                                                <span className={`badge ${getEtapaBadge(filho.etapa)}`}>
                                                                    {getEtapaLabel(filho.etapa)}
                                                                </span>
                                                            </td>
                                                            <td className="align-middle">
                                                                {/* Itens do filho */}
                                                                {empenhosDetalhados[filho.id] && empenhosDetalhados[filho.id].length > 0 && (
                                                                    <small className="text-muted">
                                                                        {empenhosDetalhados[filho.id].length} item(ns)
                                                                    </small>
                                                                )}
                                                            </td>
                                                            <td className="align-middle">
                                                                <div className="d-flex gap-1">
                                                                    <Link
                                                                        className="btn btn-sm btn-outline-info"
                                                                        to="#"
                                                                        onClick={() => {
                                                                            // Abrir detalhes na listagem geral
                                                                            window.open(`/crm/empenhos?id=${filho.id}`, '_blank');
                                                                        }}
                                                                        title="Ver Detalhes"
                                                                    >
                                                                        <i className="ti ti-eye"></i>
                                                                    </Link>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </React.Fragment>
                                                ))}

                                                {isAberto && filhos.length === 0 && (
                                                    <tr>
                                                        <td colSpan={5} className="text-center text-muted ps-4">
                                                            <small>Nenhum empenho gerado ainda. Clique em "Gerar" para criar.</small>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}

                                    {/* EMPENHOS LEGADOS (sem mestre, retrocompatibilidade) */}
                                    {legados.length > 0 && masters.length > 0 && (
                                        <tr>
                                            <td colSpan={5} className="text-muted py-2">
                                                <small className="fw-semibold">Empenhos anteriores</small>
                                            </td>
                                        </tr>
                                    )}
                                    {legados.map((empenho) => (
                                        <tr key={`legado-${empenho.id}`}>
                                            <td>
                                                <div>
                                                    <span>{empenho.codigo_empenho || `EMP-${empenho.id}`}</span>
                                                    <br />
                                                    <small className="text-muted">{formatDate(empenho.data_empenho)}</small>
                                                </div>
                                            </td>
                                            <td className="align-middle">
                                                <ProtectedValue module="AUTORIZACOES" value={formatCurrency(Number(empenho.valor_total))} />
                                            </td>
                                            <td className="align-middle">
                                                <span className={`badge ${getEtapaBadge(empenho.etapa)}`}>
                                                    {getEtapaLabel(empenho.etapa)}
                                                </span>
                                            </td>
                                            <td className="align-middle">-</td>
                                            <td className="align-middle">
                                                <div className="d-flex gap-1">
                                                    <Link
                                                        className="btn btn-sm btn-outline-primary"
                                                        to="#"
                                                        onClick={() => handleEdit(empenho)}
                                                    >
                                                        <i className="ti ti-edit"></i>
                                                    </Link>
                                                    <Link
                                                        className="btn btn-sm btn-outline-danger"
                                                        to="#"
                                                        onClick={() => handleDelete(empenho)}
                                                    >
                                                        <i className="ti ti-trash"></i>
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal para adicionar empenho mestre */}
            <ModalEmpenho
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                contratoId={contratoId}
                empenho={null}
                onSave={handleEmpenhoSaved}
            />

            {/* Modal para editar empenho */}
            {selectedEmpenho && (
                <ModalEmpenho
                    show={showEditModal}
                    onHide={() => setShowEditModal(false)}
                    contratoId={contratoId}
                    empenho={selectedEmpenho}
                    onSave={handleEmpenhoSaved}
                />
            )}

            {/* Modal para gerar empenho filho */}
            {masterParaGerar && (
                <ModalGerarEmpenho
                    show={showGerarModal}
                    onHide={() => setShowGerarModal(false)}
                    masterEmpenhoId={masterParaGerar.id}
                    masterCodigo={masterParaGerar.codigo_empenho || `EMP-${masterParaGerar.id}`}
                    onSave={handleEmpenhoSaved}
                />
            )}

            {/* Confirm Delete Modal */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
            >
                <div className="modal-body">
                    <div className="text-center">
                        <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                            <i className="ti ti-trash-x fs-36 text-danger" />
                        </div>
                        <h4 className="mb-2">Excluir Empenho?</h4>
                        <p className="mb-0">
                            Tem certeza que deseja excluir o empenho <br />
                            <strong>{selectedEmpenho?.codigo_empenho || selectedEmpenho?.numero_empenho}</strong>?
                        </p>
                        {selectedEmpenho?.is_master && (
                            <p className="text-danger mt-2">
                                <small>Este e um empenho mestre. Todos os dados de planejamento serao perdidos.</small>
                            </p>
                        )}
                        <div className="d-flex align-items-center justify-content-center mt-4">
                            <button
                                type="button"
                                className="btn btn-light me-2"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={confirmDelete}
                            >
                                Sim, Excluir
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default EmpenhosList;
